import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Перехватчик ошибок отрисовки.
 *
 * Без него любое исключение внутри компонента размонтирует всё дерево, и человек
 * видит белый экран — без объяснения, без кнопки, без надежды. Причём чаще всего
 * это случается не на пустом месте, а на чужих данных: странная картинка,
 * неожиданное поле, сломанная запись.
 *
 * Здесь мы ловим такое падение и показываем нормальный экран с выходом:
 * перезагрузить или вернуться на главную. Данные при этом целы — упала только
 * отрисовка.
 *
 * Написано классом, а не хуками: перехват ошибок отрисовки в React возможен
 * только через componentDidCatch, аналога среди хуков нет.
 */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // В консоль — чтобы разработчик увидел настоящую причину со стеком.
    console.error('[wishare] упала отрисовка:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="app-shell">
        <main className="app-main">
          <div className="empty">
            <div className="empty__icon" aria-hidden="true">
              😵‍💫
            </div>
            <div className="empty__title">Что-то сломалось на этой странице</div>
            <p className="empty__text">
              Ваши списки и желания в порядке — не открылась только сама страница.
              Попробуйте обновить или вернуться на главную.
            </p>

            <div className="row" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
              <button className="btn btn--ghost" type="button" onClick={() => window.location.reload()}>
                Обновить
              </button>
              <button
                className="btn btn--primary"
                type="button"
                onClick={() => {
                  // Полная перезагрузка, а не переход роутером: состояние
                  // приложения уже испорчено, и чинить его по частям ненадёжно.
                  window.location.href = '/';
                }}
              >
                На главную
              </button>
            </div>

            <details style={{ marginTop: 'var(--space-5)', maxWidth: 520 }}>
              <summary className="tiny faint" style={{ cursor: 'pointer' }}>
                Подробности для разработчика
              </summary>
              <pre
                className="tiny faint"
                style={{ whiteSpace: 'pre-wrap', textAlign: 'left', marginTop: 'var(--space-2)' }}
              >
                {error.message}
              </pre>
            </details>
          </div>
        </main>
      </div>
    );
  }
}
